import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Gift, ArrowLeft, Mail, CheckCircle, Loader2, Sparkles } from 'lucide-react';
import Footer from '../components/Footer';
import Header from '../components/Header';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GIFT_CARD_OPTIONS = [
  { value: 25, popular: false },
  { value: 50, popular: true },
  { value: 75, popular: false },
  { value: 100, popular: false }
];

const GiftCardsPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [selectedAmount, setSelectedAmount] = useState(50);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    recipient_name: '',
    recipient_email: '',
    sender_name: '',
    sender_email: '',
    message: ''
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    if (!formData.recipient_name || !formData.recipient_email || !formData.sender_name || !formData.sender_email) {
      toast({
        title: 'Error',
        description: 'Por favor completa todos los campos requeridos',
        variant: 'destructive'
      });
      return false;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.recipient_email) || !emailRegex.test(formData.sender_email)) {
      toast({
        title: 'Error',
        description: 'Por favor introduce emails válidos',
        variant: 'destructive'
      });
      return false;
    }
    
    return true;
  };

  const handlePurchase = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/gift-cards/purchase`, {
        amount: selectedAmount,
        recipient_name: formData.recipient_name,
        recipient_email: formData.recipient_email,
        sender_name: formData.sender_name,
        sender_email: formData.sender_email,
        message: formData.message,
        origin_url: window.location.origin
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No se recibió URL de pago');
      }
    } catch (error) {
      console.error('Error purchasing gift card:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'No se pudo procesar la compra',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />
      <div className="flex-1">
        <div className="container mx-auto px-4 py-8">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className="mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver a la tienda
          </Button>

          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="text-center mb-10">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-black rounded-full mb-4">
                <Gift className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-4xl font-bold mb-3 bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">
                Tarjetas Regalo
              </h1>
              <p className="text-gray-600 text-lg">
                El regalo perfecto para los amantes del deporte y el fitness
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left - Amount Selection */}
              <Card className="p-6">
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-black" />
                  Elige el importe
                </h2>
                
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {GIFT_CARD_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setSelectedAmount(option.value)}
                      className={`relative p-4 rounded-xl border-2 transition-all ${
                        selectedAmount === option.value
                          ? 'border-black bg-neutral-50 shadow-lg'
                          : 'border-gray-200 hover:border-neutral-300'
                      }`}
                      data-testid={`gift-card-${option.value}`}
                    >
                      {option.popular && (
                        <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-black text-white text-xs px-2 py-0.5 rounded-full">
                          Popular
                        </span>
                      )}
                      <span className="text-2xl font-bold text-gray-800">{option.value}€</span>
                    </button>
                  ))}
                </div>

                {/* Benefits */}
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-gray-800">No caduca</p>
                      <p className="text-sm text-gray-600">Usa tu tarjeta cuando quieras</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-gray-800">Envío instantáneo</p>
                      <p className="text-sm text-gray-600">Se envía por email al momento</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-gray-800">Todo el catálogo</p>
                      <p className="text-sm text-gray-600">Válida para cualquier producto</p>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Right - Recipient Form */}
              <Card className="p-6">
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <Mail className="w-5 h-5 text-black" />
                  Datos del regalo
                </h2>
                
                <div className="space-y-4">
                  <div className="bg-neutral-50 rounded-lg p-3 mb-4">
                    <p className="text-sm text-black">
                      <strong>¿Para ti?</strong> Pon tu propio email como destinatario
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="recipient_name">Nombre del destinatario *</Label>
                    <Input
                      id="recipient_name"
                      value={formData.recipient_name}
                      onChange={(e) => handleChange('recipient_name', e.target.value)}
                      placeholder="¿Quién recibirá la tarjeta?"
                      data-testid="recipient-name-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="recipient_email">Email del destinatario *</Label>
                    <Input
                      id="recipient_email"
                      type="email"
                      value={formData.recipient_email}
                      onChange={(e) => handleChange('recipient_email', e.target.value)}
                      placeholder="email@ejemplo.com"
                      data-testid="recipient-email-input"
                    />
                  </div>

                  <div className="border-t pt-4">
                    <p className="text-sm text-gray-500 mb-3">Tus datos (quien compra)</p>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="sender_name">Tu nombre *</Label>
                        <Input
                          id="sender_name"
                          value={formData.sender_name}
                          onChange={(e) => handleChange('sender_name', e.target.value)}
                          data-testid="sender-name-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="sender_email">Tu email *</Label>
                        <Input
                          id="sender_email"
                          type="email"
                          value={formData.sender_email}
                          onChange={(e) => handleChange('sender_email', e.target.value)}
                          data-testid="sender-email-input"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message">Mensaje personalizado (opcional)</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => handleChange('message', e.target.value)}
                      placeholder="¡Feliz cumpleaños! Espero que disfrutes eligiendo tu ropa favorita..."
                      rows={3}
                      data-testid="gift-message-input"
                    />
                  </div>

                  <div className="border-t pt-4">
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-gray-600">Importe de la tarjeta</span>
                      <span className="text-2xl font-bold text-black">{selectedAmount}€</span>
                    </div>
                    
                    <Button
                      onClick={handlePurchase}
                      disabled={loading}
                      className="w-full bg-black hover:bg-neutral-800 text-lg py-6"
                      data-testid="purchase-gift-card-button"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Procesando...
                        </>
                      ) : (
                        <>
                          <Gift className="w-5 h-5 mr-2" />
                          Comprar Tarjeta Regalo
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </Card>
            </div>

            {/* How it works */}
            <Card className="mt-8 p-6">
              <h2 className="text-xl font-bold mb-4 text-center">¿Cómo funciona?</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="w-12 h-12 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-xl font-bold text-black">1</span>
                  </div>
                  <h3 className="font-semibold mb-1">Elige el importe</h3>
                  <p className="text-sm text-gray-600">Selecciona entre 25€, 50€, 75€ o 100€</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-xl font-bold text-black">2</span>
                  </div>
                  <h3 className="font-semibold mb-1">Personaliza el mensaje</h3>
                  <p className="text-sm text-gray-600">Añade un mensaje especial para el destinatario</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-xl font-bold text-blue-600">3</span>
                  </div>
                  <h3 className="font-semibold mb-1">¡Listo!</h3>
                  <p className="text-sm text-gray-600">El destinatario recibe la tarjeta por email al instante</p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
      
      <Footer />
      <Toaster />
    </div>
  );
};

export default GiftCardsPage;
